import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Scissors, ArrowLeft, FileText, Package, BookOpen, Download, Shirt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import KardexMatrix from '@/components/lenceria/KardexMatrix';
import { fetchCorteById, fetchMovimientosCorte } from '@/services/cortesInventario.service';

const CorteInventarioDetailPage = () => {
  const { corteId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [corte, setCorte] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMov, setIsLoadingMov] = useState(false);
  const [activeTab, setActiveTab] = useState('resumen');

  const loadCorte = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchCorteById(corteId);
      setCorte(data);
    } catch (error) {
      console.error('Error al cargar corte:', error);
      toast({ title: 'Error', description: 'No se pudo cargar el corte', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [corteId, toast]);

  const loadMovimientos = useCallback(async () => {
    setIsLoadingMov(true);
    try {
      const response = await fetchMovimientosCorte(corteId, { limit: 10000 });
      setMovimientos(response.movimientos || []);
    } catch (error) {
      console.error('Error al cargar movimientos del corte:', error);
    } finally {
      setIsLoadingMov(false);
    }
  }, [corteId]);

  useEffect(() => {
    loadCorte();
    loadMovimientos();
  }, [loadCorte, loadMovimientos]);

  const formatFecha = (fecha) => fecha ? new Date(fecha).toLocaleDateString('es-ES') : '-';

  const columnsItems = [
    {
      id: 'tipo_prenda',
      label: 'Prenda',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shirt className="h-4 w-4 text-purple-600" />
          <span className="font-medium">{row.tipo_prenda?.nombre || 'N/A'}</span>
        </div>
      )
    },
    { id: 'cantidad_inicial', label: 'Inicial', render: (row) => <span className="text-gray-600">{row.cantidad_inicial}</span> },
    { id: 'cantidad_final', label: 'Final (sistema)', render: (row) => <span className="text-gray-600">{row.cantidad_final}</span> },
    { id: 'cantidad_real', label: 'Real (conteo)', render: (row) => <span className="font-medium">{row.cantidad_real}</span> },
    {
      id: 'diferencia',
      label: 'Diferencia',
      render: (row) => (
        <Badge variant="outline" className={
          row.diferencia > 0 ? 'bg-green-50 text-green-700'
            : row.diferencia < 0 ? 'bg-red-50 text-red-700'
            : 'bg-gray-50 text-gray-600'
        }>
          {row.diferencia > 0 ? '+' : ''}{row.diferencia}
        </Badge>
      )
    }
  ];

  const columnsMovimientos = [
    {
      id: 'fecha_movimiento',
      label: 'Fecha',
      render: (row) => new Date(row.fecha_movimiento).toLocaleString('es-ES', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      })
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo',
      render: (row) => {
        const colors = {
          entrada: 'bg-green-100 text-green-800', salida: 'bg-red-100 text-red-800',
          traslado: 'bg-blue-100 text-blue-800', ajuste: 'bg-yellow-100 text-yellow-800', baja: 'bg-gray-100 text-gray-800'
        };
        return <Badge className={`${colors[row.tipo_movimiento] || 'bg-gray-100 text-gray-800'} w-fit`}>{row.tipo_movimiento}</Badge>;
      }
    },
    {
      id: 'tipo_prenda',
      label: 'Prenda',
      render: (row) => row.tipo_prenda?.nombre || 'N/A'
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      render: (row) => <span className="font-semibold">{row.cantidad > 0 ? '+' : ''}{row.cantidad}</span>
    },
    { id: 'numero_factura', label: 'Factura/Consecutivo', render: (row) => row.numero_factura || row.consecutivo || '-' },
    { id: 'usuario', label: 'Usuario', render: (row) => row.usuario?.nombre || 'N/A' }
  ];

  const handleDownloadPDF = () => {
    if (!corte) return;
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text(`Corte de Inventario ${corte.consecutivo}`, 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(80);
    const periodo = `${corte.periodo_desde ? formatFecha(corte.periodo_desde) : 'Inicio'} → ${formatFecha(corte.periodo_hasta)}`;
    doc.text(`Fecha del corte: ${formatFecha(corte.fecha_corte)}`, 14, 26);
    doc.text(`Período: ${periodo}`, 14, 31);
    doc.text(`Usuario: ${corte.usuario?.nombre || 'N/A'}`, 14, 36);
    if (corte.notas) {
      const notas = doc.splitTextToSize(`Notas: ${corte.notas}`, 180);
      doc.text(notas, 14, 41);
    }

    const startY = corte.notas ? 41 + 6 * (Math.ceil((`Notas: ${corte.notas}`).length / 90)) : 44;

    const body = (corte.items || []).map((it) => [
      it.tipo_prenda?.nombre || 'N/A',
      it.cantidad_inicial,
      it.cantidad_final,
      it.cantidad_real,
      `${it.diferencia > 0 ? '+' : ''}${it.diferencia}`
    ]);

    const t = corte.totales || {};
    autoTable(doc, {
      startY,
      head: [['Prenda', 'Inicial', 'Final (sistema)', 'Real (conteo)', 'Diferencia']],
      body,
      foot: [['TOTALES', t.total_inicial ?? 0, t.total_final ?? 0, t.total_real ?? 0, `${(t.total_diferencia ?? 0) > 0 ? '+' : ''}${t.total_diferencia ?? 0}`]],
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229] },
      footStyles: { fillColor: [224, 231, 255], textColor: 30, fontStyle: 'bold' },
      styles: { fontSize: 9 }
    });

    doc.save(`${corte.consecutivo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (isLoading || !corte) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Scissors className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Cargando corte...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const t = corte.totales || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/lavanderia/cortes-inventario')} className="h-10 w-10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Scissors className="h-8 w-8 text-indigo-600" />
              {corte.consecutivo}
            </h1>
            <p className="text-gray-600 mt-1">
              {corte.periodo_desde ? formatFecha(corte.periodo_desde) : 'Inicio'} → {formatFecha(corte.periodo_hasta)}
              {' · '}Por {corte.usuario?.nombre || 'N/A'}
            </p>
          </div>
        </div>
        <Button onClick={handleDownloadPDF} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700">
          <Download className="h-4 w-4" />
          Descargar PDF
        </Button>
      </div>

      {corte.notas && (
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-gray-500">Notas</p>
            <p className="text-gray-800">{corte.notas}</p>
          </CardContent>
        </Card>
      )}

      {/* Resumen de totales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-4 pb-4"><p className="text-xs text-muted-foreground">Prendas</p><p className="text-xl font-bold text-gray-700">{t.total_prendas ?? 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-xs text-muted-foreground">Total Inicial</p><p className="text-xl font-bold text-gray-700">{t.total_inicial ?? 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-xs text-muted-foreground">Total Final (sistema)</p><p className="text-xl font-bold text-blue-600">{t.total_final ?? 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-xs text-muted-foreground">Total Real</p><p className="text-xl font-bold text-purple-600">{t.total_real ?? 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-4"><p className="text-xs text-muted-foreground">Diferencia</p><p className={`text-xl font-bold ${(t.total_diferencia ?? 0) > 0 ? 'text-green-600' : (t.total_diferencia ?? 0) < 0 ? 'text-red-600' : 'text-gray-500'}`}>{(t.total_diferencia ?? 0) > 0 ? '+' : ''}{t.total_diferencia ?? 0}</p></CardContent></Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Detalle del corte</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="resumen" className="flex items-center gap-2"><Package className="h-4 w-4" />Resumen</TabsTrigger>
              <TabsTrigger value="movimientos" className="flex items-center gap-2"><FileText className="h-4 w-4" />Movimientos</TabsTrigger>
              <TabsTrigger value="kardex" className="flex items-center gap-2"><BookOpen className="h-4 w-4" />Kardex</TabsTrigger>
            </TabsList>

            <TabsContent value="resumen">
              <DataTable columns={columnsItems} data={corte.items || []} isLoading={false} showDefaultActions={false} />
            </TabsContent>

            <TabsContent value="movimientos">
              <DataTable columns={columnsMovimientos} data={movimientos} isLoading={isLoadingMov} showDefaultActions={false} />
            </TabsContent>

            <TabsContent value="kardex" className="space-y-4">
              <div className="text-sm text-gray-600">
                Kardex del período: Prendas (filas) × Movimientos (columnas)
              </div>
              <KardexMatrix movimientos={movimientos} isLoading={isLoadingMov} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CorteInventarioDetailPage;
